import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase/firebaseConfig";
import { signOut } from "firebase/auth";
import { Home, User, Settings, ArrowBigLeft, LogOut } from "lucide-react";
import logotipo_recortado from "../images/logotipo-horizontal_con-nombre_recortado.png";
import freseniusLogo from "../images/fresenius-kabi_logo-negativo.png";

export default function Layout({ children }) {
  const navigate = useNavigate();

  const [fontSize, setFontSize] = useState(16);
  const [showSettings, setShowSettings] = useState(false);
  const [username, setUsername] = useState("");

  const desktopSettingsRef = useRef(null);
  const mobileSettingsRef = useRef(null);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("user");
      navigate("/");
    } catch (error) {
      console.error("Error al cerrar sesión", error);
    }
  };

  const handleFontSizeChange = (e) => {
    setFontSize(Number(e.target.value));
  };

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--app-font-size", `${fontSize}px`);
    root.style.setProperty("--base-font", `${fontSize}px`);
  }, [fontSize]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        desktopSettingsRef.current &&
        desktopSettingsRef.current.contains(e.target)
      ) return;

      if (
        mobileSettingsRef.current &&
        mobileSettingsRef.current.contains(e.target)
      ) return;

      setShowSettings(false);
    };

    if (showSettings) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSettings]);

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUsername(user.displayName || "");
    }
  }, []);

  const goBackSafe = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col">

      {/* ================= HEADER DESKTOP ================= */}
      <header className="hidden lg:block sticky top-0 z-40 bg-disfagiapp-blue text-white border-b border-black/5">
        <div className="mx-auto max-w-screen-lg h-20 px-3 grid grid-cols-3 items-center gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/perfil")}
              className="bg-white text-disfagiapp-blue-hover rounded-full p-2 shadow"
            >
              <User className="w-6 h-6" />
            </button>
            {username && (
              <span className="font-semibold truncate max-w-[200px]">
                {username}
              </span>
            )}
          </div>

          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="bg-white text-disfagiapp-blue-hover rounded-full p-2 shadow"
            >
              <Home className="w-6 h-6" />
            </button>

            <img
              src={logotipo_recortado}
              alt="DisfagiApp"
              className="h-10 object-contain rounded-md"
            />

            <button
              onClick={goBackSafe}
              className="bg-white text-disfagiapp-blue-hover rounded-full p-2 shadow"
            >
              <ArrowBigLeft className="w-6 h-6" />
            </button>
          </div>

          <div className="flex justify-end items-center gap-2 relative">
            <div ref={desktopSettingsRef} className="relative">
              <button
                onClick={() => setShowSettings((s) => !s)}
                className="bg-white text-disfagiapp-blue-hover rounded-full p-2 shadow"
              >
                <Settings className="w-6 h-6" />
              </button>

              {showSettings && (
                <div className="absolute right-0 top-12 w-56 bg-white text-gray-800 border rounded shadow-lg p-4 z-50">
                  <label className="block mb-2 font-semibold">
                    Tamaño de fuente: {fontSize}px
                  </label>
                  <input
                    type="range"
                    min={12}
                    max={48}
                    value={fontSize}
                    onChange={handleFontSizeChange}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="w-full"
                  />
                </div>
              )}
            </div>

            <button
              onClick={handleLogout}
              className="bg-white text-disfagiapp-blue-hover rounded-full p-2 shadow"
            >
              <LogOut className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* ================= HEADER MÓVIL ================= */}
      <header className="lg:hidden sticky top-0 z-40 bg-disfagiapp-blue text-white border-b border-black/5">
        <div className="h-16 grid grid-cols-3 items-center px-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/perfil")}
              className="bg-white text-disfagiapp-blue-hover rounded-full p-2 shadow shrink-0"
            >
              <User className="w-5 h-5" />
            </button>
            {username && (
              <span className="text-xs font-semibold leading-tight max-w-[160px] break-words">
                {username}
              </span>
            )}
          </div>

          <div />

          <div className="flex justify-end items-center gap-2 relative">
            <div ref={mobileSettingsRef} className="relative">
              <button
                onClick={() => setShowSettings((s) => !s)}
                className="bg-white text-disfagiapp-blue-hover rounded-full p-2 shadow"
              >
                <Settings className="w-5 h-5" />
              </button>

              {showSettings && (
                <div className="absolute right-0 top-11 w-56 max-w-[90vw] bg-white rounded shadow-lg border z-50 p-4 text-gray-800">
                  <label className="block mb-2 font-semibold">
                    Tamaño de fuente: {fontSize}px
                  </label>
                  <input
                    type="range"
                    min={12}
                    max={48}
                    value={fontSize}
                    onChange={handleFontSizeChange}
                    className="w-full"
                  />
                </div>
              )}
            </div>

            <button
              onClick={handleLogout}
              className="bg-white text-disfagiapp-blue-hover rounded-full p-2 shadow"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* ================= MAIN ================= */}
      <main
        className="flex-1 p-3 sm:p-4 lg:pb-4 pb-24"
        style={{ fontSize: "var(--app-font-size)" }}
      >
        {children}
      </main>

      {/* ================= FOOTER COLABORACIÓN DESKTOP ================= */}
      <footer className="hidden lg:block sticky bottom-0 z-20 bg-gray-50 border-t border-black/10">
        <div className="mx-auto max-w-screen-lg h-16 px-4 flex items-center justify-center gap-3 text-sm text-gray-600">
          <span>Con la colaboración de:</span>
          <img
            src={freseniusLogo}
            alt="Fresenius Kabi"
            className="h-6 object-contain"
          />
        </div>
      </footer>

      {/* ================= FOOTER MÓVIL ================= */}
      <footer className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-black/10">
        <div className="flex flex-col items-center">

          {/* NAV */}
          <div className="h-16 w-full grid grid-cols-3 items-center px-4 bg-white">
            <div className="flex justify-start">
              <button
                onClick={() => navigate("/")}
                className="bg-disfagiapp-blue text-white rounded-full p-2 shadow"
              >
                <Home className="w-5 h-5" />
              </button>
            </div>

            <div className="flex justify-center">
              <img
                src={logotipo_recortado}
                alt="DisfagiApp"
                className="h-7 object-contain"
              />
            </div>

            <div className="flex justify-end">
              <button
                onClick={goBackSafe}
                className="bg-disfagiapp-blue text-white rounded-full p-2 shadow"
              >
                <ArrowBigLeft className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* COLABORACIÓN */}
          <div className="w-full flex items-center justify-center gap-2 py-1 bg-gray-50">
            <span className="text-[10px] text-gray-400">
              Con la colaboración de:
            </span>
            <img
              src={freseniusLogo}
              alt="Fresenius Kabi"
              className="h-4 object-contain"
            />
          </div>

        </div>
      </footer>
    </div>
  );
}